import React from "react";
import { Box, Typography, alpha, keyframes, useTheme } from "@mui/material";
import { useSelector } from "react-redux";
import type { CompanyState } from "../../Store/companySlice";

// White-label loader identity, in priority order:
//   1. company.branding.favicon  (compact/circular tenant mark — preferred)
//   2. company.branding.logo     (horizontal logo — shown as ring only, not spun)
//   3. neutral rotating ring     (no tenant asset — before branding hydrates,
//                                 or when the tenant has configured neither)
//
// The favicon is the same asset used in the browser tab, so the tab icon and
// the loader always share one identity per tenant. Horizontal logos are never
// rotated: if only a logo is configured we render a neutral ring instead of
// stretching/spinning the wordmark.
const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`;

const iconGlow = keyframes`
  0%, 100% { filter: brightness(1); }
  50% { filter: brightness(1.08); }
`;

const dotPulse = keyframes`
  0%, 100% { opacity: 0.2; }
  50% { opacity: 1; }
`;

interface StepUpSpinnerProps {
  size?: "small" | "medium" | "large";
  label?: string;
}

const StepUpSpinner: React.FC<StepUpSpinnerProps> = ({ size = "medium", label }) => {
  const theme = useTheme();
  const primary = theme.palette.primary.main;

  const branding = useSelector(
    (s: any) => (s?.company as CompanyState | undefined)?.branding
  );
  const favicon = (branding?.favicon || "").trim();
  const logo = (branding?.logo || "").trim();
  // Only use the tenant asset if it's compact enough to sit inside the ring:
  // favicon is designed square, so we render it centered. A horizontal logo
  // is intentionally NOT dropped into the ring — we fall back to a neutral
  // ring so the wordmark isn't stretched or spun.
  const centerIcon = favicon || null;
  const hasLogoOnly = !favicon && !!logo;

  const config = {
    small:  { ring: 40, logo: 22, stroke: 2,   fontSize: 10 },
    medium: { ring: 64, logo: 36, stroke: 2.5, fontSize: 11.5 },
    large:  { ring: 88, logo: 50, stroke: 3,   fontSize: 12.5 },
  }[size];

  const prefersReducedMotion =
    typeof window !== "undefined" &&
    typeof window.matchMedia === "function" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  const ringAnimation = prefersReducedMotion
    ? undefined
    : `${spin} 1.2s linear infinite`;
  const iconAnimation = prefersReducedMotion
    ? undefined
    : `${iconGlow} 2.5s ease-in-out infinite`;

  return (
    <Box
      sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 1 }}
      role="status"
      aria-live="polite"
      aria-label={label || "Loading"}
    >
      <Box sx={{ position: "relative", width: config.ring, height: config.ring }}>
        <Box
          sx={{
            position: "absolute", inset: 0,
            borderRadius: "50%",
            border: `${config.stroke}px solid ${alpha(primary, 0.08)}`,
            borderTopColor: primary,
            borderRightColor: alpha(primary, 0.3),
            animation: ringAnimation,
          }}
        />

        {centerIcon && (
          <Box
            sx={{
              position: "absolute",
              top: "50%", left: "50%",
              transform: "translate(-50%, -50%)",
              width: config.logo, height: config.logo,
              display: "flex", alignItems: "center", justifyContent: "center",
              "& img": { animation: iconAnimation },
            }}
          >
            <img
              src={centerIcon}
              alt=""
              aria-hidden="true"
              onError={(e) => {
                // If the configured favicon URL is stale or 404s, don't
                // leave a broken-image icon inside the ring — hide it and
                // let the neutral ring animation carry the state.
                (e.currentTarget as HTMLImageElement).style.display = "none";
              }}
              style={{
                width: config.logo,
                height: config.logo,
                objectFit: "contain",
              }}
            />
          </Box>
        )}
      </Box>

      {hasLogoOnly && size !== "small" && (
        <Box
          sx={{
            mt: 0.5,
            maxWidth: Math.max(120, config.ring * 2),
            display: "flex", alignItems: "center", justifyContent: "center",
          }}
        >
          <img
            src={logo}
            alt=""
            aria-hidden="true"
            onError={(e) => {
              (e.currentTarget as HTMLImageElement).style.display = "none";
            }}
            style={{
              maxHeight: config.logo * 0.7,
              maxWidth: "100%",
              objectFit: "contain",
              opacity: 0.85,
            }}
          />
        </Box>
      )}

      {label && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
          <Typography sx={{ fontSize: config.fontSize, fontWeight: 500, color: "text.disabled" }}>
            {label}
          </Typography>
          {[0, 1, 2].map((i) => (
            <Box key={i} sx={{
              width: 2.5, height: 2.5, borderRadius: "50%", bgcolor: alpha(primary, 0.4),
              animation: prefersReducedMotion
                ? undefined
                : `${dotPulse} 1.2s ease-in-out ${i * 0.2}s infinite`,
            }} />
          ))}
        </Box>
      )}
    </Box>
  );
};

export default StepUpSpinner;
